'use client';

import React, { useMemo, useState } from 'react';
import { useHits } from 'react-instantsearch';
import type { SkuDTO } from '@/types/api/sku';
import type { DashboardAsk } from '@/types/api/api-v2';
import SkuCard from '@/components/common/cards/sku/SkuCard';
import { useTranslation } from 'next-i18next';
import { groupAsksBySkuAndValue } from '@/utilites/sku-operations';
import { StKilianEnum } from '@/types/translations/enums';

interface SellerBidsDisplayProps {
  asks: DashboardAsk[];
  onCardClick?: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    sku: SkuDTO,
    groupedAsks?: any
  ) => void;
  maxBottles?: number;
}

const SellerBidsDisplay: React.FC<SellerBidsDisplayProps> = ({ asks, onCardClick, maxBottles }) => {
  const { hits } = useHits<SkuDTO>();
  const { t: tCommon } = useTranslation('common');
  const { t: tStKilian } = useTranslation('st_kilian');
  const [showAll, setShowAll] = useState(false);

  // Get all grouped asks (without limit) to check if we need to show the "Show All" button
  const allGroupedAsks = useMemo(() => {
    if (!hits || hits.length === 0) {
      return [];
    }

    const searchSlugs = hits.map(hit => hit.slug);
    return groupAsksBySkuAndValue(asks, searchSlugs);
  }, [hits, asks]);

  // Filter and group asks by value based on the search results
  const groupedAsks = useMemo(() => {
    if (!hits || hits.length === 0) {
      return [];
    }

    // Get the slugs from the search results
    const searchSlugs = hits.map(hit => hit.slug);

    // Use the utility function to group asks
    return groupAsksBySkuAndValue(asks, searchSlugs, maxBottles, showAll);
  }, [hits, asks, maxBottles, showAll]);

  // Check if we should show the "Show All" button
  const shouldShowAllButton = maxBottles && allGroupedAsks.length > maxBottles;

  if (groupedAsks.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
        <p className="tw-text-center tw-text-gray-500">
          {hits.length === 0
            ? tCommon('no_results_found') || 'No results found'
            : tCommon('no_bids_available') || 'No bids available for the selected filters'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-3">
        {groupedAsks.map(group => (
          <div key={`${group.sku.slug}-${group.value}`} className="tw-relative">
            <SkuCard
              sku={{
                id: group.sku.id,
                slug: group.sku.slug,
                name: group.sku.name,
                mainImageUrl: group.sku.mainImageUrl,
                bottleSizeInMl: group.sku.bottleSizeInMl,
                lowestAsk: group.sku.lowestAsk,
                retailPrice: group.sku.retailPrice,
                distilledYear: '',
                releaseDate: group.sku.releaseDate,
                volumeInPercent: group.sku.volumeInPercent,
                numberOfBottles: null,
                recommended_order_id: null,
                rare_bottle: false,
                brand: group.sku.brand,
                category: group.sku.category,
                description: null,
                userRequestingId: null,
                export_rating: group.sku.export_rating,
                spiritoryBottle: false,
                yearBottled: null,
                orders: [],
                bids: [],
                asks: [],
                hot_deal: false,
                sku_tags: null,
                contributor: {
                  image: { url: '' },
                  name: null,
                },
                expert_review: null,
              }}
              isSellerCard={true}
              askValue={group.value}
              sellerName={group.asks[0].ownerUserId}
              askId={group.asks[0].id}
              onCardClick={(e, sku) => onCardClick?.(e, sku, group.asks)}
            />
          </div>
        ))}
      </div>

      {/* Show All Button */}
      {shouldShowAllButton && !showAll && (
        <div className="tw-mt-12 tw-flex tw-justify-center">
          <button
            onClick={() => setShowAll(true)}
            className="tw-cursor-pointer tw-rounded-lg tw-border-none tw-bg-gray-100 tw-px-6 tw-py-3 tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-200"
          >
            {tStKilian(StKilianEnum.show_all_private_casks) || 'Show All'} ({allGroupedAsks.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default SellerBidsDisplay;
